/* NAV auto-PAID — a NAV által könyvelt befizetés → a bizonylat `PAID` (9. lépés).
 *
 * ⚠⚠ A KÁR IRÁNYA ITT AZ ELLENKEZŐJE, MINT A PROJEKCIÓNÁL. Ott a hiba dupla
 * utalás; itt **ELMARADT utalás**: egy tévesen `PAID`-re állított adókötelezettség
 * eltűnik az Utalandó listáról, a tartozás megmarad, és késedelmi pótlék lesz
 * belőle — miközben a felhasználó azt hiszi, rendben van. Ezért:
 *
 *   • két lépés (terv → jóváhagyás), mint a projekciónál;
 *   • **csak a „RENDEZVE" jelölhető ki**; a részfizetés és az eltűnt tétel
 *     megjelenik, de nem választható — a felület nem tudhat olyat kérni, amit az
 *     adat nem támaszt alá (a backend is `skipped`-be teszi);
 *   • a részfizetésnél KIÍRJUK a hiányzó összeget, mert épp az a lényeg.
 *
 * ⚠ A RÉSZFIZETÉS-ESET NEM ELMÉLETI. Éles sandbox-mérés (2026-08-05): egy
 * NAV-tételen ki volt töltve a teljesítési dátum, de a befizetés 5 M Ft volt egy
 * 65,9 M Ft-os kötelezettségre. A naiv „van teljesítési dátum → ki van fizetve"
 * szabály itt 60,9 M Ft tartozást rejtett volna el.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BadgeCheck, AlertTriangle, HelpCircle, Play } from 'lucide-react';
import { navUpoApi } from '../services/api';
import { Btn, Card, Alert, Amount, Badge, Checkbox,
         ConfirmDialog } from './UI';
import { useToast } from './Toast';

const fmtDate = (v) => v ? v.replace(/-/g, '.') + '.' : '—';

/* A három javaslat-fajta. ⚠ Csak a `RENDEZVE` `auto_safe` — a másik kettő
 * megjelenik, de nem választható. */
const KIND = {
  RENDEZVE: {
    Icon: BadgeCheck, color: 'var(--success)', label: 'rendezve',
    hint: 'a NAV könyvelte a befizetést, és az fedezi a kötelezettséget',
  },
  RESZFIZETES: {
    Icon: AlertTriangle, color: 'var(--warning)', label: 'részfizetés',
    hint: 'történt fizetés, de NEM fedezi a kötelezettséget — nem lesz kifizetett',
  },
  ELTUNT_TALAN_RENDEZVE: {
    Icon: HelpCircle, color: 'var(--text-muted)', label: 'eltűnt a NAV-tól',
    hint: 'lehet rendezés, de lehet átvezetés vagy javítás is — emberi döntés',
  },
};

export default function NavSettle() {
  const toast = useToast();
  const [plan, setPlan] = useState(null);
  const [approved, setApproved] = useState({});
  const [confirm, setConfirm] = useState(null);

  const planMut = useMutation({
    mutationFn: () => navUpoApi.settlePlan(),
    onSuccess: (d) => {
      setPlan(d);
      // ⚠ Alapból CSAK az `auto_safe` van bejelölve. A többi meg sem jelölhető.
      setApproved(Object.fromEntries(
        (d.candidates || []).filter(c => c.auto_safe).map(c => [c.item_key, true])));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A lekérdezés nem sikerült.'),
  });

  const applyMut = useMutation({
    mutationFn: (keys) => navUpoApi.settleApply(keys),
    onSuccess: (d) => {
      const n = (d.updated || []).length;
      toast.success(n
        ? `${n} bizonylat kifizetettre állítva a NAV teljesítési dátumával.`
        : 'Egyetlen bizonylat sem változott.');
      setPlan(d);
      setApproved({});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A művelet nem sikerült.'),
  });

  const candidates = plan?.candidates || [];
  const safeCount = candidates.filter(c => c.auto_safe).length;
  const approvedKeys = candidates
    .filter(c => c.auto_safe && approved[c.item_key])
    .map(c => c.item_key);

  return (
    <Card title="Befizetések visszacsatolása (auto-PAID)"
      subtitle="Ha a NAV a saját folyószámláján lekönyvelte a befizetést, a hozzá tartozó bizonylat kifizetettre állítható — a NAV teljesítési dátumával."
      actions={
        <Btn size="sm" variant="ghost" loading={planMut.isPending}
          onClick={() => planMut.mutate()}>
          <Play size={14}/>Javaslatok
        </Btn>}>

      <ConfirmDialog open={!!confirm} title={confirm?.title}
        message={confirm?.message} confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const a = confirm.action; setConfirm(null); a(); }}/>

      {!plan ? (
        <Alert type="info">
          A „Javaslatok" gomb megmutatja, mely bizonylatokat lehet a NAV adatai
          alapján kifizetettre állítani. <b>Írás csak jóváhagyás után történik.</b>
        </Alert>
      ) : candidates.length === 0 ? (
        <Alert type="info">
          Nincs javaslat. Ez akkor is normális, ha még nincs a NAV-tételekhez
          párosított bizonylat — a párosítást a „Bizonylatok készítése a
          NAV-adatból" lépés végzi.
        </Alert>
      ) : (
        <>
          <div style={{display:'flex',flexDirection:'column',gap:6,
            marginBottom:12}}>
            {candidates.map(c => {
              const k = KIND[c.kind] || KIND.ELTUNT_TALAN_RENDEZVE;
              return (
                <div key={c.item_key} style={{
                  display:'flex',gap:10,alignItems:'flex-start',
                  padding:'0.55rem 0.7rem',
                  border:'1px solid var(--border)',
                  borderLeft:`3px solid ${k.color}`,
                  borderRadius:'var(--radius)',
                  opacity: c.auto_safe ? 1 : 0.75}}>

                  {/* ⚠ Csak az `auto_safe` jelölhető. A többinél nincs checkbox —
                      nem letiltott, hanem NINCS: a felület ne sugallja, hogy
                      egy kattintással kifizetettre lehetne állítani. */}
                  <div style={{width:22,paddingTop:2}}>
                    {c.auto_safe && (
                      <Checkbox label="" checked={!!approved[c.item_key]}
                        onChange={e => setApproved(p => ({
                          ...p, [c.item_key]: e.target.checked }))}/>
                    )}
                  </div>

                  <k.Icon size={15} style={{color:k.color,flexShrink:0,
                    marginTop:2}}/>

                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',gap:8,alignItems:'center',
                      flexWrap:'wrap'}}>
                      <span style={{fontSize:'0.8rem',fontWeight:600}}>
                        {k.label}
                      </span>
                      <span style={{fontSize:'0.72rem',color:'var(--text-muted)',
                        fontFamily:'var(--mono)'}}>
                        adónem {c.adonem_kod}
                      </span>
                      <Badge>{c.doc_status}</Badge>
                      {c.teljesitesi_datum && (
                        <span style={{fontSize:'0.72rem',
                          color:'var(--text-muted)'}}>
                          teljesítés: {fmtDate(c.teljesitesi_datum)}
                        </span>)}
                    </div>
                    <div style={{fontSize:'0.74rem',color:'var(--text-dim)',
                      marginTop:2}}>{c.indok}</div>
                  </div>

                  <div style={{textAlign:'right',whiteSpace:'nowrap',
                    fontSize:'0.76rem'}}>
                    <div>kötelezettség: <Amount value={c.kotelezettseg ?? 0}/></div>
                    {c.befizetes != null && (
                      <div style={{color:'var(--success)'}}>
                        befizetés: <Amount value={c.befizetes}/>
                      </div>)}
                    {/* ⚠ EZ A LÉNYEG a részfizetésnél: mennyi maradt. */}
                    {c.hiany > 0 && (
                      <div style={{color:'var(--danger)',fontWeight:600}}>
                        hiány: <Amount value={c.hiany}/>
                      </div>)}
                  </div>
                </div>
              );
            })}
          </div>

          {safeCount < candidates.length && (
            <Alert type="info" style={{marginBottom:12}}>
              {candidates.length - safeCount} javaslat <b>nem állítható
              automatikusan</b> kifizetettre. A részfizetésnél a NAV könyvelt
              befizetést, de az nem fedezi a kötelezettséget — kifizetettre
              állítva a maradék tartozás eltűnne a látókörből, és késedelmi
              pótlék lenne belőle.
            </Alert>
          )}

          {(plan.updated?.length > 0 || plan.skipped?.length > 0) && (
            <Alert type={plan.dry_run ? 'info' : 'success'}
              style={{marginBottom:12}}>
              {plan.dry_run
                ? `Terv: ${plan.updated.length} bizonylat állítható kifizetettre.`
                : `${plan.updated.length} bizonylat kifizetettre állítva.`}
              {plan.skipped?.length > 0 &&
                ` ${plan.skipped.length} kihagyva.`}
            </Alert>
          )}

          <div style={{display:'flex',justifyContent:'space-between',
            alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
              {approvedKeys.length} / {safeCount} jóváhagyva
            </span>
            <div style={{display:'flex',gap:8}}>
              <Btn variant="ghost" onClick={() => setPlan(null)}>Mégse</Btn>
              <Btn disabled={!approvedKeys.length} loading={applyMut.isPending}
                onClick={() => setConfirm({
                  title: 'Bizonylatok kifizetettre állítása',
                  confirmLabel: 'Kifizetettre állítás',
                  message: `${approvedKeys.length} bizonylat kifizetettre `
                         + 'állítása a NAV teljesítési dátumával. Ezek eltűnnek '
                         + 'az Utalandó listáról, tehát nem fogod őket kifizetni. '
                         + 'Folytatod?',
                  action: () => applyMut.mutate(approvedKeys),
                })}>
                Jóváhagyás
              </Btn>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
